import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { RootState } from 'features/rootReducer';
import { ReactNode } from 'react';
import { ToastType } from 'types/common/toast';

interface State {
  open: boolean;
  type?: ToastType;
  content?: ReactNode;
  autoHide?: boolean;
}

const initialState: State = {
  open: false,
  autoHide: false,
};

// [EN] Callers often pass a raw API error body (`error.data`), which can be an object.
//      Rendering an object as a React child throws, and since the toast is mounted at
//      the App root, that one throw blanks the whole admin.
// [KO] 호출부가 API 에러 body(`error.data`)를 그대로 넘기는 경우가 많고, 이게 객체일 수 있다.
//      객체를 React child 로 렌더하면 throw 하는데, 토스트가 App 최상단에 붙어 있어서
//      그 한 번의 throw 로 어드민 전체가 흰 화면이 된다.
export const toToastText = (content: unknown): string => {
  if (typeof content === 'string' && content) return content;
  if (typeof content === 'number') return String(content);
  if (content && typeof content === 'object') {
    const { resultMessage, message } = content as { resultMessage?: unknown; message?: unknown };
    if (typeof resultMessage === 'string' && resultMessage) return resultMessage;
    if (typeof message === 'string' && message) return message;
  }
  return 'Internal Error';
};

export const toastSlice = createSlice({
  name: 'toast',
  initialState,
  reducers: {
    toastMessage: (
      state,
      {
        payload: { content, autoHide = false, toastType = ToastType.ERROR },
      }: PayloadAction<{ content: string; autoHide?: boolean; toastType?: ToastType }>,
    ) => {
      state.type = toastType;
      state.open = true;
      state.content = toToastText(content);
      state.autoHide = autoHide;
    },
    errorResponse: (state, { payload: { response } }: PayloadAction<AxiosError>) => {
      state.type = ToastType.ERROR;
      state.open = true;

      if (!response) {
        state.content = 'Internal Error: No response';
        return;
      }
      const data = response.data as { message?: string } | undefined;
      if (!data?.message) {
        state.content = 'Internal Error';
        return;
      }

      state.content = data.message;
    },
    close: state => {
      state.open = false;
      state.autoHide = false;
    },
  },
});

export const toastAction = toastSlice.actions;
export const toastReducer = toastSlice.reducer;

export const selectToastOpen = ({ toast: { open } }: RootState): boolean => open;
export const selectToastType = ({ toast: { type } }: RootState): ToastType | undefined => type;
export const selectToastContent = ({ toast: { content } }: RootState): ReactNode | undefined =>
  content;
export const selectToastAutoHide = ({ toast: { autoHide } }: RootState): boolean | undefined =>
  autoHide;
